import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@journ/ui/card'
import { Label } from '@journ/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@journ/ui/select'
import { Switch } from '@journ/ui/switch'
import { locales } from '@/features/i18n/domain/locale'
import { useLocale } from '@/features/i18n/hooks/use-locale'
import { useUserPreferences } from '@/features/preferences/hooks/use-user-preferences'
import { useIntlayer } from 'react-intlayer'
import type { Locale } from '@/features/i18n/domain/locale'
import type { UserPreferenceDateFormat, UserPreferenceTimeFormat } from '@/features/preferences/domain/user-preferences'

const dateFormats: UserPreferenceDateFormat[] = ['dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd', 'dd.MM.yyyy']
const timeFormats: UserPreferenceTimeFormat[] = [12, 24]
const timezones = ['UTC', 'America/Sao_Paulo', 'America/New_York', 'Europe/Lisbon', 'Europe/London', 'Europe/Paris']

const detectDeviceTimezone = () => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
	} catch {
		return 'UTC'
	}
}

const LocaleCard = () => {
	const content = useIntlayer('date-locale-settings')
	const { locale, setLocale } = useLocale()
	const { updatePreferences } = useUserPreferences()
	const labels: Record<Locale, string> = { en: content.locale.en.value, 'pt-BR': content.locale['pt-BR'].value }

	const handleChange = (value: Locale) => {
		setLocale(value)
		void updatePreferences({ locale: value })
	}

	return (
		<Card className='flex justify-between items-center gap-6'>
			<CardHeader>
				<CardTitle>{content.locale.title}</CardTitle>
				<CardDescription>{content.locale.description}</CardDescription>
			</CardHeader>
			<CardContent>
				<Select value={locale} onValueChange={handleChange}>
					<SelectTrigger className='w-45'>
						<SelectValue placeholder={content.locale.placeholder.value}>{labels[locale]}</SelectValue>
					</SelectTrigger>
					<SelectContent>
						{locales.map(value => (
							<SelectItem key={value} value={value}>
								{labels[value]}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</CardContent>
		</Card>
	)
}

const TimezoneCard = () => {
	const content = useIntlayer('date-locale-settings')
	const { preferences, updatePreferences } = useUserPreferences()
	const deviceTimezone = detectDeviceTimezone()
	const currentTimezone = preferences.timezoneAutoSync ? deviceTimezone : preferences.timezone

	const handleAutoSyncChange = (enabled: boolean) => {
		void updatePreferences({ timezoneAutoSync: enabled, ...(enabled ? { timezone: deviceTimezone } : {}) })
	}

	const handleTimezoneChange = (value: string) => {
		void updatePreferences({ timezone: value, timezoneAutoSync: false })
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>{content.timezone.title}</CardTitle>
				<CardDescription>{content.timezone.description}</CardDescription>
			</CardHeader>
			<CardContent className='space-y-6'>
				<div className='flex items-center justify-between gap-6'>
					<div className='space-y-1'>
						<Label htmlFor='timezone-auto-sync'>{content.timezone.auto}</Label>
						<div className='text-[0.8rem] text-muted-foreground'>
							{content.timezone.autoDescription}
							<div>
								{content.timezone.currentDevice}: {deviceTimezone}
							</div>
						</div>
					</div>
					<Switch
						id='timezone-auto-sync'
						checked={preferences.timezoneAutoSync}
						onCheckedChange={handleAutoSyncChange}
					/>
				</div>

				{!preferences.timezoneAutoSync && (
					<div className='space-y-2'>
						<Label>{content.timezone.manualLabel}</Label>
						<Select value={currentTimezone} onValueChange={handleTimezoneChange}>
							<SelectTrigger className='w-60'>
								<SelectValue placeholder={content.timezone.placeholder.value} />
							</SelectTrigger>
							<SelectContent>
								{timezones.map(timezone => (
									<SelectItem key={timezone} value={timezone}>
										{timezone}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
			</CardContent>
		</Card>
	)
}

const TimeFormatCard = () => {
	const content = useIntlayer('date-locale-settings')
	const { preferences, updatePreferences } = useUserPreferences()

	const handleChange = (value: string) => {
		void updatePreferences({ timeFormat: Number(value) as UserPreferenceTimeFormat })
	}

	return (
		<Card className='flex justify-between items-center gap-6'>
			<CardHeader>
				<CardTitle>{content.timeFormat.title}</CardTitle>
				<CardDescription>{content.timeFormat.description}</CardDescription>
			</CardHeader>
			<CardContent>
				<Select value={preferences.timeFormat.toString()} onValueChange={handleChange}>
					<SelectTrigger className='w-45'>
						<SelectValue placeholder={content.timeFormat.placeholder.value} />
					</SelectTrigger>
					<SelectContent>
						{timeFormats.map(value => (
							<SelectItem key={value} value={value.toString()}>
								{value === 12 ? content.timeFormat.twelve : content.timeFormat.twentyFour}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</CardContent>
		</Card>
	)
}

const DateFormatCard = () => {
	const content = useIntlayer('date-locale-settings')
	const { preferences, updatePreferences } = useUserPreferences()

	const handleChange = (value: UserPreferenceDateFormat) => {
		void updatePreferences({ dateFormat: value })
	}

	return (
		<Card className='flex justify-between items-center gap-6'>
			<CardHeader>
				<CardTitle>{content.dateFormat.title}</CardTitle>
				<CardDescription>{content.dateFormat.description}</CardDescription>
			</CardHeader>
			<CardContent>
				<Select value={preferences.dateFormat} onValueChange={handleChange}>
					<SelectTrigger className='w-45'>
						<SelectValue placeholder={content.dateFormat.placeholder.value} />
					</SelectTrigger>
					<SelectContent>
						{dateFormats.map(value => (
							<SelectItem key={value} value={value}>
								{value}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</CardContent>
		</Card>
	)
}

const WeekCard = () => {
	const content = useIntlayer('date-locale-settings')
	const { preferences, updatePreferences } = useUserPreferences()

	return (
		<Card className='flex justify-between items-center gap-6'>
			<CardHeader>
				<CardTitle>{content.week.title}</CardTitle>
				<CardDescription>{content.week.description}</CardDescription>
			</CardHeader>
			<CardContent>
				<Switch
					checked={preferences.weekStartsOnMonday}
					onCheckedChange={weekStartsOnMonday => void updatePreferences({ weekStartsOnMonday })}
				/>
			</CardContent>
		</Card>
	)
}

const DateLocaleSettings = () => (
	<div className='space-y-6 mt-4'>
		<LocaleCard />
		<TimezoneCard />
		<TimeFormatCard />
		<DateFormatCard />
		<WeekCard />
	</div>
)

export { DateLocaleSettings }
